import React, { useState, Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import AppLayout from "./components/Layout";
import { ConfigProvider } from "antd";
import AppContext from "./utilities/contexts/appContext";
import Loading from "./components/Loading";
import { GoogleOAuthProvider } from "@react-oauth/google";
import Guest from "./components/pages/guest/Guest";
import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import { DirectionType } from "antd/es/config-provider";

import { UnifiedServiceProvider } from "./utilities/contexts/UnifiedServiceContext";
import UnifiedService from "./components/services/UnifiedService";
import { clearAgreement } from "./utilities/helpers/agreementHelper";
import {
  BODY_MEASUREMENT_CONFIG,
  AUTO_TAGGING_CONFIG,
  VISUAL_CLOTHES_SEARCH_CONFIG,
  GARMENT_MEASUREMENT_CONFIG,
  DEMO_BODY_MEASUREMENT_CONFIG,
  DEMO_AUTO_TAGGING_CONFIG,
  DEMO_VISUAL_CLOTHES_SEARCH_CONFIG,
  DEMO_GARMENT_MEASUREMENT_CONFIG,
} from "./utilities/config/service-configs";

import Gate from "./components/pages/sdk/Gate";
import SdkLayout from "./components/layouts/SdkLayout";
import SdkAccessDenied from "./components/pages/sdk/SdkAccessDenied";
import SdkNotFound from "./components/pages/sdk/SdkNotFound";
import SdkUnifiedService from "./components/pages/sdk/SdkUnifiedService";

import NotFound from "./components/pages/NotFound";
import "./App.css";
import "antd/dist/reset.css";
import "./locales/i18n";
import GuestServices from "./components/pages/guest/GuestServices";
import { initializeRefId } from "./utilities/helpers/refIdHelper";

i18next.use(initReactI18next).init({
  lng: "en", // Set the default language here
  fallbackLng: "en", // Fallback language if translation not found
  interpolation: {
    escapeValue: false, // React already escapes strings, so no need to escape again
  },
});

// Component to initialize ref_id from URL
const RefIdInitializer: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    initializeRefId();
  }, [location.search]); // Re-run when query params change

  return null;
};

function App() {
  const Login = React.lazy(() => import("./components/pages/Login"));
  const Register = React.lazy(() => import("./components/pages/Register"));
  const AddCharacteristic = React.lazy(() => import("./components/pages/AddCharacteristic"));
  const Profile = React.lazy(() => import("./components/pages/Profile"));
  const Dashboard = React.lazy(() => import("./components/pages/Dashboard"));
  const PersonList = React.lazy(() => import("./components/pages/person/PersonList"));
  const PersonProfile = React.lazy(() => import("./components/pages/person/PersonProfile"));
  const AddPerson = React.lazy(() => import("./components/pages/person/AddPerson"));
  const EditPerson = React.lazy(() => import("./components/pages/person/EditPerson"));
  const EditProfile = React.lazy(() => import("./components/pages/EditProfile"));
  const ServiceList = React.lazy(() => import("./components/pages/services/ServiceList"));
  const ResetPassword = React.lazy(() => import("./components/pages/ResetPassword"));

  const [step, setStep] = useState(0);
  const [token, setToken] = useState<string | null>(localStorage.getItem("token"));
  const [isLoading, setLoading] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [direction, setDirection] = useState<DirectionType>("ltr");
  const [user, setUser] = useState<any>(null);
  const [lang, setLang] = useState("en");

  const handleLang = (lang: string) => {
    setLang(lang);
  };

  const handleLogin = (token: string) => {
    setToken(token);
    localStorage.setItem("token", token);
  };
  const handleLogout = () => {
    setToken(null);
    localStorage.removeItem("token");
    clearAgreement();
    setUser(null);
  };
  const handleStep = (num: number) => {
    setStep(num);
  };
  const handleCapturing = (value: boolean) => {
    setCapturing(value);
  };
  const handleLoading = (loading: boolean) => {
    setLoading(loading);
  };
  const handleDirection = (direction: DirectionType) => {
    setDirection(direction);
  };
  const handleUser = (user: any) => {
    setUser(user);
  };

  /*  555255542706-6r2amf101t9fi03apkvpignu3dgf6caj.apps.googleusercontent.com  */
  return (
    <GoogleOAuthProvider clientId="555255542706-6r2amf101t9fi03apkvpignu3dgf6caj.apps.googleusercontent.com">
      <AppContext.Provider
        value={{
          handleLoading,
          handleUser,
          user,
          isLoading,
          step,
          capturing,
          handleCapturing,
          handleStep,
          handleDirection,
          direction,
          token,
          lang,
          handleLang,
          handleLogin,
          handleLogout,
        }}
      >
        <ConfigProvider
          direction={direction}
          theme={{
            token: {
              colorPrimary: "#474BCF",
              fontFamily: lang === "fa" ? "iranYekan" : "PlusJakartaSans",
            },
          }}
        >
          {isLoading && <Loading />}
          <BrowserRouter>
            <RefIdInitializer />
            <Routes>
              <Route index element={!!user && !!token ? <Navigate to={`/${i18next.language}/dashboard/`} replace={true} /> : <Navigate to={`/${i18next.language}/guest`} />} />
              <Route
                path="auth/password-reset"
                element={
                  <Suspense fallback={<Loading />}>
                    <ResetPassword />
                  </Suspense>
                }
              />
              <Route element={<AppLayout />} path=":lang">
                <Route index element={!!user && !!token ? <Navigate to={`/${i18next.language}dashboard/`} /> : <Navigate to={`/${i18next.language}/guest`} />} />
                <Route
                  path="login"
                  element={
                    <Suspense fallback={<Loading />}>
                      <Login />
                    </Suspense>
                  }
                />
                <Route
                  path="auth/password-reset"
                  element={
                    <Suspense fallback={<Loading />}>
                      <ResetPassword />
                    </Suspense>
                  }
                />
                <Route
                  path="register"
                  element={
                    <Suspense fallback={<Loading />}>
                      <Register />
                    </Suspense>
                  }
                />
                <Route
                  path="setInformation"
                  element={
                    <Suspense fallback={<Loading />}>
                      <AddCharacteristic />
                    </Suspense>
                  }
                />
                <Route
                  path="dashboard"
                  element={
                    <Suspense fallback={<Loading />}>
                      <Dashboard />
                    </Suspense>
                  }
                >
                  <Route path="persons">
                    <Route
                      index
                      element={
                        <Suspense fallback={<Loading />}>
                          <PersonList />
                        </Suspense>
                      }
                    />
                    <Route
                      path=":personId"
                      element={
                        <Suspense fallback={<Loading />}>
                          <PersonProfile />
                        </Suspense>
                      }
                    />
                    <Route
                      path=":personId/edit"
                      element={
                        <Suspense fallback={<Loading />}>
                          <EditPerson />
                        </Suspense>
                      }
                    />
                    <Route
                      path="addPerson"
                      element={
                        <Suspense fallback={<Loading />}>
                          <AddPerson />
                        </Suspense>
                      }
                    />
                  </Route>
                  <Route path="services">
                    <Route
                      index
                      element={
                        <Suspense fallback={<Loading />}>
                          <ServiceList />
                        </Suspense>
                      }
                    />
                    <Route
                      path="body-measurement"
                      element={
                        <Suspense fallback={<Loading />}>
                          <UnifiedServiceProvider config={BODY_MEASUREMENT_CONFIG}>
                            <UnifiedService />
                          </UnifiedServiceProvider>
                        </Suspense>
                      }
                    />
                    <Route
                      path="auto-tagging"
                      element={
                        <Suspense fallback={<Loading />}>
                          <UnifiedServiceProvider config={AUTO_TAGGING_CONFIG}>
                            <UnifiedService />
                          </UnifiedServiceProvider>
                        </Suspense>
                      }
                    />
                    {/* <Route
                      path="visual-clothes-search"
                      element={
                        <Suspense fallback={<Loading />}>
                          <UnifiedServiceProvider config={VISUAL_CLOTHES_SEARCH_CONFIG}>
                            <UnifiedService />
                          </UnifiedServiceProvider>
                        </Suspense>
                      }
                    /> */}
                    <Route
                      path="garment-measurement"
                      element={
                        <Suspense fallback={<Loading />}>
                          <UnifiedServiceProvider config={GARMENT_MEASUREMENT_CONFIG}>
                            <UnifiedService />
                          </UnifiedServiceProvider>
                        </Suspense>
                      }
                    />
                  </Route>

                  <Route
                    path="editProfile"
                    element={
                      <Suspense fallback={<Loading />}>
                        <EditProfile />
                      </Suspense>
                    }
                  />
                  <Route
                    index
                    element={
                      <Suspense fallback={<Loading />}>
                        <Profile />
                      </Suspense>
                    }
                  />
                </Route>
                <Route path="guest">
                  <Route index element={<Guest />} />
                  <Route path="services" element={<GuestServices />} />
                  <Route
                    path="body-measurement"
                    element={
                      <Suspense fallback={<Loading />}>
                        <UnifiedServiceProvider config={DEMO_BODY_MEASUREMENT_CONFIG}>
                          <UnifiedService />
                        </UnifiedServiceProvider>
                      </Suspense>
                    }
                  />
                  <Route
                    path="auto-tagging"
                    element={
                      <Suspense fallback={<Loading />}>
                        <UnifiedServiceProvider config={DEMO_AUTO_TAGGING_CONFIG}>
                          <UnifiedService />
                        </UnifiedServiceProvider>
                      </Suspense>
                    }
                  />
                  <Route
                    path="visual-clothes-search"
                    element={
                      <Suspense fallback={<Loading />}>
                        <UnifiedServiceProvider config={DEMO_VISUAL_CLOTHES_SEARCH_CONFIG}>
                          <UnifiedService />
                        </UnifiedServiceProvider>
                      </Suspense>
                    }
                  />
                  <Route
                    path="garment-measurement"
                    element={
                      <Suspense fallback={<Loading />}>
                        <UnifiedServiceProvider config={DEMO_GARMENT_MEASUREMENT_CONFIG}>
                          <UnifiedService />
                        </UnifiedServiceProvider>
                      </Suspense>
                    }
                  />
                </Route>
              </Route>

              <Route path="sdk" element={<SdkLayout />}>
                <Route index element={<Gate />} />
                <Route path="unifiedService" element={<SdkUnifiedService />} />

                <Route path="accessDenied" element={<SdkAccessDenied />} />
                <Route path="*" element={<SdkNotFound />} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </ConfigProvider>
      </AppContext.Provider>
    </GoogleOAuthProvider>
  );
}

export default App;
